import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Search, Plus, ShoppingCart, Upload, FileText, Eye, Loader2, CheckCircle, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import FDVSnapshotView from './sales/FDVSnapshotView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending_validation: { label: 'En attente validation', class: 'bg-amber-100 text-amber-700 border-amber-200' },
  valide_en_attente: { label: 'Valide - En attente 30j', class: 'bg-blue-100 text-blue-700 border-blue-200' },
  devis_signe: { label: 'Devis signe', class: 'bg-blue-100 text-blue-700 border-blue-200' },
  facture_envoyee: { label: 'Facture envoyee', class: 'bg-purple-100 text-purple-700 border-purple-200' },
  facture_soumise: { label: 'Facture soumise', class: 'bg-purple-100 text-purple-700 border-purple-200' },
  paiement_recu: { label: 'Paiement recu', class: 'bg-teal-100 text-teal-700 border-teal-200' },
  securisation: { label: 'En securisation', class: 'bg-amber-100 text-amber-700 border-amber-200' },
  commission_eligible: { label: 'Commission eligible', class: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  en_attente_paiement: { label: 'En attente paiement', class: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  paye: { label: 'Paye', class: 'bg-green-100 text-green-800 border-green-200' },
  rejete: { label: 'Rejete', class: 'bg-red-100 text-red-700 border-red-200' },
  rejected: { label: 'Rejete', class: 'bg-red-100 text-red-700 border-red-200' },
  annule: { label: 'Annule', class: 'bg-red-100 text-red-700 border-red-200' },
};

export const MySales = () => {
  const { isAdmin } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSale, setSelectedSale] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedSales, setSelectedSales] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [saleDocuments, setSaleDocuments] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSales();
  }, []);

  const fetchSales = async () => {
    try {
      const response = await fetch(`${API}/sales`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSales(data);
      }
    } catch (error) {
      console.error('Failed to fetch sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSale) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas depasser 10 Mo');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        const response = await fetch(`${API}/invoices`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            sale_id: selectedSale.sale_id,
            file_name: file.name,
            file_data: base64,
            file_type: file.name.split('.').pop().toLowerCase()
          }),
        });

        if (response.ok) {
          const data = await response.json();
          toast.success(`Facture ${data.invoice_number} uploadee`);
          setIsUploadOpen(false);
          fetchSales();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Erreur lors de l\'upload');
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur de connexion');
      setUploading(false);
    }
  };

  const openSaleDetail = (sale) => {
    setSelectedSale(sale);
    setSaleDocuments([]);
    setIsDetailOpen(true);
    (async () => {
      try {
        const r = await fetch(`${API}/sales/${sale.sale_id}/documents`, { credentials: 'include' });
        if (r.ok) setSaleDocuments(await r.json());
      } catch (e) { /* noop */ }
    })();
  };

  const downloadDocument = async (docId, filename) => {
    setDownloading(docId);
    try {
      const r = await fetch(`${API}/sales/${selectedSale.sale_id}/documents/${docId}/download`, { credentials: 'include' });
      if (r.ok) {
        const blob = await r.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click();
        window.URL.revokeObjectURL(url); a.remove();
      } else {
        toast.error('Erreur lors du telechargement');
      }
    } catch (e) {
      toast.error('Erreur lors du telechargement');
    } finally {
      setDownloading(null);
    }
  };

  const openUploadModal = (sale) => {
    setSelectedSale(sale);
    setIsUploadOpen(true);
  };

  const toggleSaleSelection = (saleId) => {
    setSelectedSales(prev => 
      prev.includes(saleId) 
        ? prev.filter(id => id !== saleId)
        : [...prev, saleId]
    );
  };

  const toggleAllSales = () => {
    if (selectedSales.length === filteredSales.length) {
      setSelectedSales([]);
    } else {
      setSelectedSales(filteredSales.map(s => s.sale_id));
    }
  };

  const deleteSelectedSales = async () => {
    if (selectedSales.length === 0) return;
    
    if (!window.confirm(`Supprimer ${selectedSales.length} vente(s) ? Cette action est irreversible.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      const response = await fetch(`${API}/sales/bulk`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(selectedSales),
      });
      
      if (response.ok) {
        toast.success(`${selectedSales.length} vente(s) supprimee(s)`);
        setSelectedSales([]);
        fetchSales();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setDeleting(false);
    }
  };

  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.client_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalAmount = filteredSales.reduce((sum, sale) => sum + sale.amount, 0);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="h-64 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="my-sales-title">
            Mes ventes
          </h1>
          <p className="text-slate-500 mt-1">Suivez l'etat de vos ventes</p>
        </div>
        <Link to="/declare-sale">
          <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-sale-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle vente
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-indigo-100">
              <ShoppingCart className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total ventes</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">{filteredSales.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <span className="text-emerald-600 font-bold text-lg">EUR</span>
            </div>
            <div>
              <p className="text-sm text-slate-500">Montant total</p>
              <p className="text-xl font-bold text-slate-900 dark:text-white">
                {totalAmount.toLocaleString('fr-FR')} EUR
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Admin: Delete selected */}
      {isAdmin && selectedSales.length > 0 && (
        <div className="flex items-center gap-4 p-4 bg-red-50 rounded-lg border border-red-200">
          <span className="text-sm text-red-700">
            {selectedSales.length} vente(s) selectionnee(s)
          </span>
          <Button
            variant="destructive"
            size="sm"
            onClick={deleteSelectedSales}
            disabled={deleting}
            data-testid="delete-selected-btn"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Supprimer
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher un client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-sales-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="status-filter">
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Sales table */}
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="sales-table-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                {isAdmin && (
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={selectedSales.length === filteredSales.length && filteredSales.length > 0}
                      onCheckedChange={toggleAllSales}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                )}
                <TableHead className="font-semibold">Client</TableHead>
                {isAdmin && <TableHead className="font-semibold">Commercial</TableHead>}
                <TableHead className="font-semibold">Offre</TableHead>
                <TableHead className="font-semibold">Montant</TableHead>
                <TableHead className="font-semibold">Statut</TableHead>
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 9 : 7} className="text-center py-8 text-slate-500">
                    Aucune vente trouvee
                  </TableCell>
                </TableRow>
              ) : (
                filteredSales.map((sale, index) => (
                  <TableRow 
                    key={sale.sale_id} 
                    className={`table-row-hover animate-fade-in ${selectedSales.includes(sale.sale_id) ? 'bg-red-50' : ''}`}
                    style={{ animationDelay: `${index * 0.02}s` }}
                    data-testid={`sale-row-${sale.sale_id}`}
                  >
                    {isAdmin && (
                      <TableCell>
                        <Checkbox
                          checked={selectedSales.includes(sale.sale_id)}
                          onCheckedChange={() => toggleSaleSelection(sale.sale_id)}
                          data-testid={`select-sale-${sale.sale_id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-medium">{sale.client_name}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="text-sm">
                          <p className="font-medium">{sale.commercial_name || '-'}</p>
                          {sale.commercial_email && (
                            <p className="text-xs text-slate-400">{sale.commercial_email}</p>
                          )}
                        </div>
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge variant="outline">{sale.offer}</Badge>
                    </TableCell>
                    <TableCell className="font-semibold">
                      {sale.amount.toLocaleString('fr-FR')} EUR
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statusConfig[sale.status]?.class} border`}>
                        {statusConfig[sale.status]?.label || sale.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {new Date(sale.created_at).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openSaleDetail(sale)}
                        title="Voir les details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Sale detail modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Details de la vente</DialogTitle>
            <DialogDescription>
              {selectedSale?.client_name}
            </DialogDescription>
          </DialogHeader>
          {selectedSale && (
            <div className="space-y-6 py-4">
              {/* Main info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-medium">{selectedSale.client_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Offre</p>
                  <p className="font-medium">{selectedSale.offer}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Montant</p>
                  <p className="font-medium text-emerald-600">
                    {selectedSale.amount?.toLocaleString('fr-FR')} EUR
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Statut</p>
                  <Badge className={`${statusConfig[selectedSale.status]?.class} border`}>
                    {statusConfig[selectedSale.status]?.label}
                  </Badge>
                </div>
                {selectedSale.academy && (
                  <div>
                    <p className="text-sm text-slate-500">Academie</p>
                    <p className="font-medium">{selectedSale.academy}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-500">Date</p>
                  <p className="font-medium">
                    {new Date(selectedSale.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>
              
              {/* Dynamic FDV snapshot rendering */}
              {selectedSale.template_snapshot && (
                <FDVSnapshotView
                  snapshot={selectedSale.template_snapshot}
                  formValues={selectedSale.form_values || {}}
                  documents={saleDocuments}
                  onDownload={downloadDocument}
                  downloadingId={downloading}
                />
              )}

              {/* Declaration data if available (legacy fallback) */}
              {!selectedSale.template_snapshot && selectedSale.declaration_data && (
                <>
                  {/* Etablissement */}
                  {selectedSale.declaration_data.establishment && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-slate-700 mb-3">Etablissement</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-slate-400">Nom</p>
                          <p>{selectedSale.declaration_data.establishment.name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Type</p>
                          <p>{selectedSale.declaration_data.establishment.type || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Region</p>
                          <p>{selectedSale.declaration_data.establishment.region || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Adresse</p>
                          <p>
                            {selectedSale.declaration_data.establishment.address?.street || '-'}
                            {selectedSale.declaration_data.establishment.address?.city && 
                              `, ${selectedSale.declaration_data.establishment.address.city}`}
                            {selectedSale.declaration_data.establishment.address?.postal && 
                              ` ${selectedSale.declaration_data.establishment.address.postal}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Contact */}
                  {selectedSale.declaration_data.contact && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-slate-700 mb-3">Contact</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-slate-400">Nom</p>
                          <p>{selectedSale.declaration_data.contact.name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Fonction</p>
                          <p>{selectedSale.declaration_data.contact.function || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Email</p>
                          <p>{selectedSale.declaration_data.contact.email || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Telephone</p>
                          <p>{selectedSale.declaration_data.contact.phone || '-'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Offre details */}
                  {selectedSale.declaration_data.offer && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-slate-700 mb-3">Details Offre</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-slate-400">Duree</p>
                          <p>{selectedSale.declaration_data.offer.duration || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Prix TTC</p>
                          <p>{selectedSale.declaration_data.offer.price_ttc || '-'} EUR</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Remise</p>
                          <p>{selectedSale.declaration_data.offer.discount_percent || 0}%</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Total TTC</p>
                          <p className="font-semibold text-emerald-600">
                            {selectedSale.declaration_data.offer.total_ttc || '-'} EUR
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Paiement */}
                  {selectedSale.declaration_data.payment && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-slate-700 mb-3">Paiement</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-slate-400">Mode</p>
                          <p>{selectedSale.declaration_data.payment.mode || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Type</p>
                          <p>{selectedSale.declaration_data.payment.type || '-'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Upload invoice section */}
              {!selectedSale.has_invoice && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-sm font-medium text-amber-800 mb-2">
                    Facture non uploadee
                  </p>
                  <Button
                    onClick={() => {
                      setIsDetailOpen(false);
                      openUploadModal(selectedSale);
                    }}
                    className="w-full"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Uploader la facture
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload invoice modal */}
      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Uploader une facture</DialogTitle>
            <DialogDescription>
              {selectedSale?.client_name} - {selectedSale?.amount?.toLocaleString('fr-FR')} EUR
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-8 border-2 border-dashed rounded-xl text-center">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg"
              />
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-600 mb-2">
                Glissez votre facture ici ou
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Parcourir
              </Button>
              <p className="text-xs text-slate-400 mt-4">
                Formats acceptes: PDF, PNG, JPG (max 10 Mo)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUploadOpen(false)}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
